import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Inbox, FolderOpen, Grid3x3, Gauge, Plus, Search, RefreshCw,
  AlertTriangle, Clock, ArrowUpDown, X, Mail, Trash2, Download,
} from 'lucide-react';
import Ficha from './Ficha';
import Catalogo from './Catalogo';
import Cobertura from './Cobertura';
import PanelControl from './PanelControl';
import { CorreosTab } from './emails';
import { deadlineInfo, formatRemaining } from '../../../lib/deadline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ESTADOS = [
  'NUEVA_SOLICITUD', 'EN_EXCLUSIONES', 'EN_VERIFICACION', 'EN_CRIBADO',
  'EN_DIAGNOSTICO', 'PENDIENTE_DECISION', 'DECIDIDO',
];
const ESTADO_LABEL = {
  NUEVA_SOLICITUD: 'Nueva solicitud', EN_EXCLUSIONES: 'En exclusiones',
  EN_VERIFICACION: 'En verificación', EN_CRIBADO: 'En cribado',
  EN_DIAGNOSTICO: 'En diagnóstico', PENDIENTE_DECISION: 'Pendiente decisión',
  DECIDIDO: 'Decidido',
};
const ORIGENES = ['C1 - Contacto directo', 'C2 - Referido', 'C3 - Frio'];
const EVALUADORES = ['Pablo', 'Roberto', 'Ambos'];
const RESULTADOS = ['Admitido', 'En reserva', 'Rechazado'];
const VERTICALES = [
  { key: 'v1_relevo', label: 'V1 · Relevo' },
  { key: 'v2_internacionalizacion', label: 'V2 · Internacionalización' },
  { key: 'v3_financiera', label: 'V3 · Financiera' },
  { key: 'v4_crecimiento', label: 'V4 · Crecimiento' },
];
const RESULT_STYLE = {
  'Admitido': 'text-emerald-400 bg-emerald-400/10',
  'En reserva': 'text-amber-400 bg-amber-400/10',
  'Rechazado': 'text-red-400 bg-red-400/10',
};

function ResultBadge({ value }) {
  if (!value || value === 'Sin puntuar') return <span className="text-white/20 text-xs">—</span>;
  const s = RESULT_STYLE[value] || 'text-white/50 bg-white/5';
  const label = value === 'Rechazado' ? 'No admitido' : value;
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${s}`}>{label}</span>;
}

function AddCandidateModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', linkedin_url: '', origen: '', evaluador: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/candidates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, origen: form.origen || null, evaluador: form.evaluador || null }),
      });
      if (res.ok) { onCreated(); onClose(); } else setError('No se pudo crear el candidato');
    } catch { setError('Error de conexión'); }
    setSaving(false);
  };
  const inputCls = 'w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/25 outline-none focus:border-[#C58C40]/50';
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" data-testid="add-candidate-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-[#0d1b2a] border border-white/10 rounded-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white text-base font-medium">Añadir candidato a mano</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <input className={inputCls} placeholder="Nombre *" value={form.nombre} onChange={(e) => set('nombre', e.target.value)} data-testid="add-candidate-nombre" />
          <input className={inputCls} placeholder="Email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          <input className={inputCls} placeholder="Teléfono" value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
          <input className={inputCls} placeholder="LinkedIn URL" value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} />
          <select className={inputCls} value={form.origen} onChange={(e) => set('origen', e.target.value)}>
            <option value="">Origen…</option>{ORIGENES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select className={inputCls} value={form.evaluador} onChange={(e) => set('evaluador', e.target.value)}>
            <option value="">Evaluador…</option>{EVALUADORES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button type="submit" disabled={saving} data-testid="add-candidate-submit"
            className="w-full bg-[#C58C40] text-white py-2.5 rounded text-sm tracking-wide uppercase hover:bg-[#b07b35] transition-colors disabled:opacity-50">
            {saving ? 'Creando…' : 'Crear candidato'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Bandeja({ token, onSelect, plazoHoras, onChanged }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filters, setFilters] = useState({ estado: '', origen: '', resultado: '', evaluador: '', vertical: '' });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'plazo', dir: 'asc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    if (search.trim()) params.set('q', search.trim());
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/candidates?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setCandidates(d.candidates); }
    } catch (e) { if (process.env.NODE_ENV !== 'production') console.error(e); }
    setLoading(false);
    if (onChanged) onChanged();
  }, [token, filters, search, onChanged]);

  useEffect(() => { const t = setTimeout(fetchData, 250); return () => clearTimeout(t); }, [fetchData]);

  const plazoRemaining = (c) => {
    if (c.estado !== 'NUEVA_SOLICITUD') return Infinity;
    const info = deadlineInfo(c.created_at, plazoHoras, false);
    return info ? info.remainingHours : Infinity;
  };

  const sorted = useMemo(() => {
    const arr = [...candidates];
    const getV = (c) => {
      switch (sort.key) {
        case 'id': return c.id || '';
        case 'nombre': return (c.nombre || '').toLowerCase();
        case 'origen': return c.origen || '';
        case 'estado': return c.estado || '';
        case 'resultado': return c.computed.resultado_display || '';
        case 'evaluador': return c.evaluador || '';
        case 'dias': return c.computed.dias_desde_solicitud ?? -1;
        case 'plazo': return plazoRemaining(c);
        default: return '';
      }
    };
    arr.sort((a, b) => {
      const va = getV(a), vb = getV(b);
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [candidates, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  const selectCls = 'bg-white/5 border border-white/10 rounded px-2.5 py-1.5 text-white text-xs outline-none focus:border-[#C58C40]/50';

  const deleteCandidate = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar el candidato ${id}? Esta acción no se puede deshacer.`)) return;
    await fetch(`${BACKEND_URL}/api/admin/candidates/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchData();
  };

  const exportXlsx = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/export/advisors-xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'advisors.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  const Th = ({ label, k }) => (
    <th className="px-3 py-2.5 text-left font-medium">
      <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 text-white/50 hover:text-white transition-colors">
        {label}<ArrowUpDown size={11} className={sort.key === k ? 'text-[#C58C40]' : 'text-white/20'} />
      </button>
    </th>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2.5 mb-5">
        <div className="flex-1 min-w-[180px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, área, sector, empresa…" data-testid="bandeja-search"
            className="w-full pl-9 pr-3 py-1.5 bg-white/5 border border-white/10 rounded text-white text-xs outline-none focus:border-[#C58C40]/50" />
        </div>
        <select className={selectCls} value={filters.estado} onChange={(e) => setFilters((f) => ({ ...f, estado: e.target.value }))} data-testid="filter-estado">
          <option value="">Estado</option>{ESTADOS.map((s) => <option key={s} value={s}>{ESTADO_LABEL[s]}</option>)}
        </select>
        <select className={selectCls} value={filters.origen} onChange={(e) => setFilters((f) => ({ ...f, origen: e.target.value }))} data-testid="filter-origen">
          <option value="">Origen</option>{ORIGENES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={selectCls} value={filters.resultado} onChange={(e) => setFilters((f) => ({ ...f, resultado: e.target.value }))} data-testid="filter-resultado">
          <option value="">Resultado</option>{RESULTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={selectCls} value={filters.evaluador} onChange={(e) => setFilters((f) => ({ ...f, evaluador: e.target.value }))} data-testid="filter-evaluador">
          <option value="">Evaluador</option>{EVALUADORES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={selectCls} value={filters.vertical} onChange={(e) => setFilters((f) => ({ ...f, vertical: e.target.value }))} data-testid="filter-vertical">
          <option value="">Vertical</option>{VERTICALES.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
        <button onClick={fetchData} className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors" title="Actualizar">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
        <button onClick={() => setShowAdd(true)} data-testid="add-candidate-btn"
          className="flex items-center gap-1.5 bg-[#C58C40] text-white rounded px-3 py-1.5 text-xs tracking-wide hover:bg-[#b07b35] transition-colors">
          <Plus size={14} />Añadir candidato
        </button>
        <button onClick={exportXlsx} data-testid="bandeja-export-xlsx"
          className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded px-3 py-1.5 text-xs text-white/70 hover:text-white transition-colors">
          <Download size={14} />Exportar Excel
        </button>
      </div>

      <div className="border border-white/5 rounded overflow-x-auto" data-testid="bandeja-table">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] border-b border-white/5">
            <tr>
              <th className="px-3 py-2.5 w-8"></th>
              <Th label="ID" k="id" /><Th label="Nombre" k="nombre" /><Th label="Origen" k="origen" />
              <Th label="Estado" k="estado" /><Th label="Plazo" k="plazo" />
              <Th label="Resultado" k="resultado" /><Th label="Evaluador" k="evaluador" /><Th label="Días" k="dias" />
              <th className="px-3 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading && sorted.length === 0 ? (
              <tr><td colSpan={10} className="text-white/30 text-center py-12">Cargando…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={10} className="text-white/30 text-center py-12">No hay candidatos</td></tr>
            ) : sorted.map((c) => {
              const cm = c.computed;
              const stopped = c.estado !== 'NUEVA_SOLICITUD';
              const dInfo = deadlineInfo(c.created_at, plazoHoras, stopped);
              return (
                <tr key={c.id} data-testid={`candidate-row-${c.id}`} onClick={() => onSelect(c.id)}
                  className="border-b border-white/5 hover:bg-white/[0.05] transition-colors cursor-pointer">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      {cm.excluido && <span title="Exclusión activa" data-testid={`badge-exclusion-${c.id}`}><AlertTriangle size={13} className="text-red-400" /></span>}
                      {cm.stale && <span title="Parado más de 7 días" data-testid={`badge-stale-${c.id}`}><Clock size={13} className="text-amber-400" /></span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-white/60 font-mono">{c.id}</td>
                  <td className="px-3 py-2.5 text-white font-medium">{c.nombre || '—'}</td>
                  <td className="px-3 py-2.5 text-white/50">{c.origen || '—'}</td>
                  <td className="px-3 py-2.5 text-white/60">{ESTADO_LABEL[c.estado] || c.estado}</td>
                  <td className="px-3 py-2.5" data-testid={`plazo-badge-${c.id}`}>
                    {stopped ? (
                      <span className="text-white/20 text-xs">—</span>
                    ) : dInfo ? (
                      <span className={`text-xs px-2 py-0.5 rounded ${dInfo.urgency.bg} ${dInfo.urgency.color}`}>
                        {dInfo.urgency.label || formatRemaining(dInfo.remainingHours)}
                      </span>
                    ) : <span className="text-white/20 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2.5"><ResultBadge value={cm.resultado_display} /></td>
                  <td className="px-3 py-2.5 text-white/50">{c.evaluador || '—'}</td>
                  <td className="px-3 py-2.5 text-white/50">{cm.dias_desde_solicitud === null ? '—' : `${cm.dias_desde_solicitud}d`}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={(e) => deleteCandidate(e, c.id)} title="Eliminar candidato" data-testid={`delete-candidate-${c.id}`}
                      className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-red-400 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-white/25 text-xs mt-3">{sorted.length} candidato(s) · pulsa una fila para abrir la ficha</p>

      {showAdd && <AddCandidateModal token={token} onClose={() => setShowAdd(false)} onCreated={fetchData} />}
    </div>
  );
}

const SUB_TABS = [
  { id: 'bandeja', label: 'Bandeja', icon: Inbox },
  { id: 'catalogo', label: 'Catálogo', icon: FolderOpen },
  { id: 'cobertura', label: 'Cobertura', icon: Grid3x3 },
  { id: 'control', label: 'Panel de control', icon: Gauge },
  { id: 'correos', label: 'Correos', icon: Mail },
];

export default function AdvisorsModule({ token, initialCandidateId, onChanged }) {
  const [sub, setSub] = useState('bandeja');
  const [config, setConfig] = useState(null);
  const [selectedId, setSelectedId] = useState(initialCandidateId || null);
  const [catFilter, setCatFilter] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${BACKEND_URL}/api/admin/config`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setConfig(await res.json());
    })();
  }, [token]);

  if (selectedId && config) {
    return <Ficha token={token} config={config} candidateId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div>
      <div className="flex gap-1 mb-6 border-b border-white/5">
        {SUB_TABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)} data-testid={`advisors-subtab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs transition-colors border-b-2 ${
              sub === t.id ? 'text-[#C58C40] border-[#C58C40]' : 'text-white/40 border-transparent hover:text-white/70'
            }`}>
            <t.icon size={14} /><span>{t.label}</span>
          </button>
        ))}
      </div>
      {sub === 'bandeja' && <Bandeja token={token} onSelect={setSelectedId} plazoHoras={config?.plazo_horas || 48} onChanged={onChanged} />}
      {sub === 'catalogo' && <Catalogo token={token} initialFilter={catFilter} />}
      {sub === 'cobertura' && <Cobertura token={token} onCellClick={(f) => { setCatFilter(f); setSub('catalogo'); }} />}
      {sub === 'control' && <PanelControl token={token} />}
      {sub === 'correos' && <CorreosTab token={token} />}
    </div>
  );
}
